import * as sort from "enums/sort"
import { createRef, useCallback, useMemo, useRef, useState } from "react"

import { Store } from "store"
import { TextFieldProps } from "@mui/material"

import * as util from "lib/util"
import {
	FieldFilter,
	MccApi,
	SearchFilters,
	SearchPayload,
} from "lib/api"
import * as dateFilter from "enums/dateFilter"
import { useStore } from "react-redux"
import { FilterEnum } from "enums/filterEnum"


export const DateFilterDelimiter = "~"

function createSearchPayload<T, F extends Record<string, any>>(
	filters: SearchFilters<T, F>,
	filterEnums: FilterEnum<keyof F, any>[],
	getDateFiltersForPayload: (
		field: keyof SearchFilters<T, F>,
		filters: SearchFilters<T, F>
	) => FieldFilter<F>[]
): SearchPayload<T, F> {
	const payload: SearchPayload<T, F> = {
		sortList: filters.sort.MakeSortItem(),
		paging: filters.paging,
		resultList: filters.resultList,
		searchFilters: {
			andOrOperator: "AND",
			// distinct: true,
			filterGroups: [],
			fieldFilters: [],
		},
	}

	// Get non-query filters first
	const regularFilters: FieldFilter<F>[] = []

	// Use for..of for better performance
	for (const [k, v] of filters.filters.Entries()) {
		if (k === "q" || k === "query" || !v || v.toString().IsEmpty()) {
			continue
		}

		const vStr = String(v)
		const hasMultipleVals = vStr.Contains(util.SearchQueryDelimiter)
		const filterValue = hasMultipleVals ? "" : vStr
		const filterValueList = hasMultipleVals ? vStr.Split(util.SearchQueryDelimiter) : []

		const filterEnum = filterEnums.Find((f) => f.Data.field === k)
		if (!filterEnum.IsSome()) {
			throw new Error("Invalid filter key")
		}

		// Special case: impactedSectorTypes is a collection, so joining can duplicate results.
		// Ensure DISTINCT when filtering on this path.
		if (String(k).Equal("impactedSectorTypes.sectorType.sector.id")) {
			payload.searchFilters.distinct = true as any
		}

		regularFilters.Append(
			filterEnum.Unwrap().GetFieldFilter({ filterValue, filterValueList })
		)
	}

	// Apply date filters if not All Time to optimize query performance
	if (!filters.createDate.Is("AllTime")) {
		regularFilters.Append(...getDateFiltersForPayload("createDate", filters));
	}
	if (!filters.updateDate.Is("AllTime")) {
		regularFilters.Append(...getDateFiltersForPayload("updateDate", filters));
	}

	// Handle query if present
	const query = filters.filters.q || filters.filters.query
	if (query) {
		payload.searchFilters.filterGroups = payload.searchFilters.filterGroups || []
		payload.searchFilters.filterGroups.push({
			andOrOperator: "OR",
			filterGroups: [],
			fieldFilters: filterEnums.Map((f) =>
				f.GetFieldFilter({ filterValue: query, filterOperator: "contains" })
			),
		} as any)
	}

	payload.searchFilters.fieldFilters = regularFilters

	return payload as SearchPayload<T, F>
}

export function UseSearchFilters<T, F extends Record<string, any>, K extends keyof F = keyof F>(
	filterEnums: FilterEnum<K, any>[],
	defaultField: keyof F = "createDate"
) {
	// Memoize fields extraction
	const fields = useMemo(() => filterEnums.Map((f) => f.Data.field), [filterEnums])

	const [fieldParams, setFieldParams] = useState<F>({} as F)
	const [sortParam, setSortParam] = useState<sort.SortEnum>(sort.Desc(defaultField.String()))
	const [dateParams, setDateParams] = useState<{
		createDate: dateFilter.DateFilter
		updateDate: dateFilter.DateFilter
	}>({
		createDate: dateFilter.AllTime(),
		updateDate: dateFilter.AllTime(),
	})
	const [pageParams, setPageParams] = useState<{ page: int; pageSize: int }>({
		page: 1,
		pageSize: 25,
	})

	// Create refs only once
	const filterRefs = useRef<Record<keyof F, { current: HTMLInputElement | null }>>(
		(() => {
			const refs: any = {}
			fields.Range((_, field) => {
				refs[field] = createRef()
			})
			refs.q = createRef()
			return refs
		})()
	)

	// Memoize filters object with better dependency tracking
	const filters = useMemo<SearchFilters<T, F>>(() => {
		return {
			filters: fieldParams,
			paging: pageParams,
			sort: sortParam,
			createDate: dateParams.createDate,
			updateDate: dateParams.updateDate,
			resultList: [],
		}
	}, [fieldParams, sortParam, pageParams, dateParams])

	// Memoize individual functions with stable references
	const getFilterVal = useCallback((k: K) => filters.filters[k], [filters.filters])

	const clearFilter = useCallback(
		(k: string) => () => {
			if (filterEnums.Any((f) => f.IsFilterKey(k))) {
				setFieldParams({ [k]: null } as any)
				if (filterRefs.current.q?.current) {
					filterRefs.current.q.current.value = ""
				}
			} else {
				throw new Error(`Invalid filter key: ${k}`)
			}
		},
		[filterEnums, setFieldParams]
	)

	const getInputProps = useCallback(
		(k: K): TextFieldProps => ({
			inputRef: (ref) => {
				if (ref) {
					filterRefs.current[k].current = ref
				}
			},
			defaultValue: getFilterVal(k),
			onChange: (e) => setFieldParams({ [k]: e.target.value } as any),
		}),
		[getFilterVal, setFieldParams]
	)

	const filtersActive = useMemo<boolean>(() => {
		return (
			filters.filters.Entries().Any(([k, v]) => !isNil(v)) ||
			filters.Entries().Any(([key, v]) => dateFilter.IsDateFilter(v) && !v.Is("AllTime"))
		)
	}, [filters])

	const [editingField, setEditingField] = useState<keyof F | null>(null)

	// Memoize date filter generation
	const getDateFiltersForPayload = useCallback(
		(field: keyof SearchFilters<T, F>, filters: SearchFilters<T, F>): FieldFilter<F>[] => {
			const format = "yyyy-MM-DD HH:mm:ss"
			const date = filters[field]
			if (dateFilter.IsDateFilter(date)) {
				return [
					{
						fieldName: field,
						filterValue: date.GetStartDate().utc().format(format),
						filterOperator: "gte",
						filterValueList: [],
					},
					{
						fieldName: field,
						filterValue: date.GetEndDate().utc().format(format),
						filterOperator: "lte",
						filterValueList: [],
					},
				]
			} else {
				return []
			}
		},
		[]
	)

	const [refreshKey, setRefreshKey] = useState(0)

	const searchPayload = useMemo<SearchPayload<T, F>>(() => {
		return createSearchPayload(filters, filterEnums, getDateFiltersForPayload)
	}, [
		// Use specific dependencies instead of entire objects
		JSON.stringify(filters.filters), // Serialize to ensure deep comparison
		filters.sort,
		filters.paging.page,
		filters.paging.pageSize,
		filters.createDate,
		filters.updateDate,
		refreshKey,
		filterEnums,
		getDateFiltersForPayload,
	])

	const refresh = useCallback(() => {
		setRefreshKey((p) => p + 1)
	}, [])

	const resetFilters = useCallback(() => {
		if (filterRefs.current) {
			Object.entries(filterRefs.current).forEach!(([key, ref]) => {
				if (ref?.current) {
					ref.current.value = ""
					ref.current.defaultValue = ""
				}
			})
		}
		setFieldParams({} as F)
		setSortParam(sort.Desc(defaultField.String()))
		setDateParams({ createDate: dateFilter.AllTime(), updateDate: dateFilter.AllTime() })
		setPageParams({ page: 1, pageSize: 25 })
	}, [defaultField])

	const setDateParam = useCallback(
		(field: keyof F, v: dateFilter.DateFilter) => {
			setDateParams((prev) => ({ ...prev, [field]: v }))
		},
		[]
	)

	// CRITICAL: Memoize setFilter to prevent re-renders
	const setFilter = useCallback(
		(updates: Partial<F>) => {
			setFieldParams((prev) => ({ ...prev, ...updates }))
		},
		[]
	)

	// Return memoized context value
	return useMemo(
		() => ({
			filters,
			filtersActive,
			setSortParam,
			setDateParam,
			dateParams,
			setFilter, // Use the memoized version
			resetFilters,
			clearFilter,
			getFilterVal,
			filterRefs,
			getInputProps,
			setPageParams,
			setEditingField,
			editingField,
			searchPayload,
			refresh,
		}),
		[
			filters,
			filtersActive,
			setSortParam,
			setDateParam,
			dateParams,
			setFilter,
			resetFilters,
			clearFilter,
			getFilterVal,
			filterRefs,
			getInputProps,
			setPageParams,
			setEditingField,
			editingField,
			searchPayload,
			refresh,
		]
	)
}


export function UseMccApi() {
	const store = useStore() as Store
	return MccApi.GetInstance(store)
}

